import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { BASE_URL } from '../../utils/config';
import eyeIcon from '../../images/eye.svg';
import eyeOffIcon from '../../images/eye-closed.svg';

const ResetPassword = ({ setHideFooter }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  
  const [showPasswords, setShowPasswords] = useState({
    newPassword: false,
    confirmPassword: false
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (setHideFooter) {
      setHideFooter(true);
    }
    
    // Check if token exists
    if (!token) {
      toast.error(t('resetPassword.invalidToken'));
      navigate('/auth/login?mode=login');
    }
    
    return () => {
      // Polling disabled to prevent property changes during animation
      if (setHideFooter) {
        setHideFooter(false);
      }
    };
  }, [token, navigate, setHideFooter]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = t('resetPassword.validation.newPasswordRequired');
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = t('resetPassword.validation.passwordMinLength');
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = t('resetPassword.validation.confirmPasswordRequired');
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('resetPassword.validation.passwordsDontMatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/users/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token,
          newPassword: formData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || t('resetPassword.success'), {
          autoClose: 3000,
          position: "bottom-right"
        });
        
        // Clear form
        setFormData({
          newPassword: '',
          confirmPassword: ''
        });
        
        // Redirect to login
        setTimeout(() => {
          navigate('/auth/login?mode=login');
        }, 2000);
      } else {
        toast.error(data.message || t('resetPassword.error'));
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(t('resetPassword.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(2px)',
      WebkitBackdropFilter: 'blur(5px)'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '12px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
        maxWidth: '450px',
        width: 'calc(100% - 40px)',
        textAlign: 'center',
        fontFamily: 'Arial, sans-serif',
        boxSizing: 'border-box',
        margin: '20px'
      }}>
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{
            margin: '0 0 10px 0',
            color: '#333',
            fontSize: '28px',
            fontWeight: '600'
          }}>
            {t('resetPassword.title')}
          </h2>
          <p style={{
            color: '#666',
            fontSize: '16px',
            lineHeight: '1.5',
            marginBottom: '30px'
          }}>
            {t('resetPassword.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* New Password */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#333',
              fontWeight: '500'
            }}>
              {t('resetPassword.newPassword')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPasswords.newPassword ? 'text' : 'password'}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder={t('resetPassword.newPassword')}
                required
                style={{
                  width: '100%',
                  padding: '12px 45px 12px 12px',
                  border: `2px solid ${errors.newPassword ? '#e74c3c' : '#e1e5e9'}`,
                  borderRadius: '6px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#29B185'}
                onBlur={(e) => e.target.style.borderColor = errors.newPassword ? '#e74c3c' : '#e1e5e9'}
              />
              {formData.newPassword && (
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('newPassword')}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#8B5CF6',
                    fontSize: '18px',
                    padding: '4px'
                  }}
                >
                  <img 
                    src={showPasswords.newPassword ? eyeOffIcon : eyeIcon} 
                    alt={t('registration.togglePasswordVisibility')}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                </button>
              )}
            </div>
            {errors.newPassword && (
              <p style={{ color: '#e74c3c', fontSize: '14px', margin: '5px 0 0 0' }}>
                {errors.newPassword}
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#333',
              fontWeight: '500'
            }}>
              {t('resetPassword.confirmPassword')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPasswords.confirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={t('resetPassword.confirmPassword')}
                required
                style={{
                  width: '100%',
                  padding: '12px 45px 12px 12px',
                  border: `2px solid ${errors.confirmPassword ? '#e74c3c' : '#e1e5e9'}`,
                  borderRadius: '6px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#29B185'}
                onBlur={(e) => e.target.style.borderColor = errors.confirmPassword ? '#e74c3c' : '#e1e5e9'}
              />
              {formData.confirmPassword && (
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirmPassword')}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#8B5CF6',
                    fontSize: '18px',
                    padding: '4px'
                  }}
                >
                  <img 
                    src={showPasswords.confirmPassword ? eyeOffIcon : eyeIcon} 
                    alt={t('registration.togglePasswordVisibility')}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                </button>
              )}
            </div>
            {errors.confirmPassword && (
              <p style={{ color: '#e74c3c', fontSize: '14px', margin: '5px 0 0 0' }}>
                {errors.confirmPassword}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: isLoading ? '#ccc' : '#29B185',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.3s ease',
              marginBottom: '20px'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#238a6b';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#29B185';
              }
            }}
          >
            {isLoading ? t('common.saving') : t('resetPassword.submitButton')}
          </button>
        </form>

        <div style={{ marginTop: '30px' }}>
          <button
            onClick={() => navigate('/auth/login?mode=login')}
            style={{
              color: '#29B185',
              textDecoration: 'none',
              fontSize: '16px',
              fontWeight: '500',
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
